import formatUTCDate from '@/shared/tools/formatUTCDate'
import type { ContactSubscriptionData } from './contactSubscriptionData'

type ItalyContactInformation = {
  document_type?: string
  document_number?: string
  expiration_date?: string
  created_date?: string
  issuing_entity?: string
  issuing_city?: string
  tax_id_code?: string
}

type BackendComment = {
  comment?: string
  created_at: string
}

type BackendContactApiData = {
  born_city: string
  italy_contact_information?: ItalyContactInformation
  zoho_id?: string
  firstname?: string
  firstname2?: string
  lastname?: string
  lastname2?: string
  birthday?: string
  civility?: string
  civility2?: string
  email?: string
  phone1?: string
  phone2?: string
  is_professional?: number
  disclaimer?: number
  newsletter?: boolean
  ekomi_interaction?: string
  whatsapp_consent?: string | number
  web_callback?: string
  bac_voucher?: boolean
  pro_social_reason?: string
  pro_siret?: string
  pro_activity?: string
  pro_ape_code?: string
  comments?: BackendComment[]
  created_at?: string
  referral_firstname?: string
  referral_lastname?: string
  advisor_calendly_link?: string
  mcp_advisory_id?: number | string | null
}

export const mapContactSubscriptionData = (
  currentContract: BackendContactApiData,
): Omit<ContactSubscriptionData, 'config'> & {
  config: Omit<ContactSubscriptionData['config'], 'identityModal' | 'fiscalCodeIsLoading' | 'fiscalCodeIsValid'>
} => {
  const identityInfo: ItalyContactInformation = currentContract.italy_contact_information ?? {}

  return {
    config: {
      errors: {},
      contractLoaded: true,
      isSaving: false,
      emailIsLoading: false,
      isDirty: false,
      prefilledEmail: {
        status: false,
        message: '',
      },
      siretIsLoading: false,
      siretIsValid: false,
      newsletterData: {},
      newsletterLoading: false,
      newsletterError: '',
      newsletterCanBeSent: true,
      isValidZohoId: false,
      isMcpClient: !!currentContract.advisor_calendly_link,
      isMcpLocked: false,
      mcpUnlockError: null,
    },
    personal: {
      zohoId: currentContract.zoho_id ?? '',
      firstName: currentContract.firstname ?? '',
      firstName2: currentContract.firstname2 ?? '',
      lastName: currentContract.lastname ?? '',
      lastName2: currentContract.lastname2 ?? '',
      birthdate: currentContract.birthday ?? '',
      civility: currentContract.civility ?? '',
      civility2: currentContract.civility2 ?? '',
      email: currentContract.email ?? '',
      phone1: currentContract.phone1 ?? '',
      phone2: currentContract.phone2 ?? '',
      secondName: (currentContract.lastname2?.length ?? 0) > 0 && (currentContract.firstname2?.length ?? 0) > 0,
      bornCity: currentContract.born_city ?? '',
      mcpAdvisoryId: currentContract.mcp_advisory_id ?? null,
    },
    preferences: {
      clientType: currentContract.is_professional === 1 ? 'pro' : 'part',
      dataCollectionAuth: currentContract.disclaimer === 1,
      neverBounceResult: (currentContract.email?.length ?? 0) > 0 ? 'valid' : '',
      isNewsletterRegistered: currentContract.newsletter ?? false,
      isEkomiRegistered: (currentContract.ekomi_interaction?.length ?? 0) > 0,
      whatsappConsent: !!currentContract.whatsapp_consent,
      advisorCalendlyLink: currentContract.advisor_calendly_link ?? '',
      callbackDate: currentContract.web_callback ? currentContract.web_callback.split('T')[0] : '',
      callbackTime: currentContract.web_callback ? currentContract.web_callback.split('T')[1] : '',
      bacVoucher: currentContract.bac_voucher ?? false,
      createEkomiInteraction: null,
    },
    professional: {
      socialReason: currentContract.pro_social_reason ?? '',
      siret: currentContract.pro_siret ?? '',
      activity: currentContract.pro_activity ?? '',
      apeCode: currentContract.pro_ape_code ?? '',
    },
    additional: {
      comments: getLastComment(currentContract.comments ?? []),
      createdAt: currentContract.created_at ?? '',
      referral: !!(currentContract.referral_firstname && currentContract.referral_lastname),
      referralFirstName: currentContract.referral_firstname ?? '',
      referralLastName: currentContract.referral_lastname ?? '',
    },
    identity: {
      documentType: identityInfo.document_type ?? '',
      documentNumber: identityInfo.document_number ?? '',
      expirationDate: identityInfo.expiration_date ? formatUTCDate(identityInfo.expiration_date, 10) : '',
      createdDate: identityInfo.created_date ? formatUTCDate(identityInfo.created_date, 10) : '',
      issuingEntity: identityInfo.issuing_entity ?? '',
      issuingCity: identityInfo.issuing_city ?? '',
      taxIdCode: identityInfo.tax_id_code ?? '',
    },
  }
}

export const filterLastComment = (comments: BackendComment[]): string => {
  if (!Array.isArray(comments)) {
    return comments
  }
  let lastCommentTime = new Date(comments[0].created_at).getTime()
  let lastComment = comments[0].comment

  comments.forEach(item => {
    if (new Date(item.created_at).getTime() >= lastCommentTime) {
      lastCommentTime = new Date(item.created_at).getTime()
      lastComment = item.comment
    }
  })
  return lastComment ?? ''
}

export const getLastComment = (comments: BackendComment[]): string => {
  if (comments.length === 0) {
    return ''
  }
  if (comments.length === 1) {
    return comments[0].comment ?? ''
  }
  return filterLastComment(comments)
}
